import { Button } from '@mui/material';
import { Component, ReactElement, ReactNode, RefObject } from 'react';
import { DocumentStatus, ReceiptAlert } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import {
    PaloBreadcrumb,
    PaloConfirmationDialog,
    PaloDetailPreview,
    PaloDetailView,
    PaloDocumentPreview,
    PaloOverflowAction,
    PaloOverflowMenu,
    PaloShortcutBinding,
    PaloStatusPill,
} from '@synergy/components-palo';
import { TransitionIcon } from '@synergy/theme-next';
import { WithRouterProps, withRouter } from '@synergy/components';
import { getDocumentStatusTranslationId, padZeroLeft } from '@synergy/utils';

import { ConverterReceiptModal } from '../convert-receipt/converter-receipt-modal';
import { Demand } from '../demands/models/demand';
import { Inquiry } from '../inquiries/models/inquiry';
import { Invoice } from '../invoices/models/invoice';
import { PaloDocumentNotices } from '../../common/components/palo/palo-document-notices';
import { PaloFlowAction, PaloSaveAction, paloDocumentPhase } from './palo-main-action';
import { PoolStep } from '../../common/models/step';
import { PoolStepper } from '../../common/components/stepper/pool-stepper';
import { PurchaseOrder } from '../purchase-orders/models/purchaseOrder';
import { SalesOrder } from '../sales-orders/models/salesOrder';
import { Transition } from '../convert-receipt/models/type-transition';
import { TransitionSource } from '../convert-receipt/models/type-transition-source';
import { TransitionTarget } from '../convert-receipt/models/internal/transition-target';

interface Document {
    id: number;
    status: DocumentStatus;
    receiptAlert?: ReceiptAlert;
}

type EntityType = Document & (Inquiry | Invoice | PurchaseOrder | SalesOrder | Demand);

interface MyPaloDetailHeaderProps<TEntity extends EntityType> {
    entity?: TEntity;
    steps: PoolStep[];
    /** See PoolStepper: a saved document opens its steps in any order. */
    enforceStepOrder?: boolean;
    stepIndex?: number;
    stepsDone?: number[];
    stepperInitialState?: TEntity;
    viewOffers?: boolean;
    contentPortal?: Element | null;
    currency: string;
    isSuperAdmin: boolean;
    unlocked: boolean;
    converterAvailable?: boolean;
    /** Why a follow-up cannot be made yet; shown on the greyed-out entry. */
    transitionBlockedReason?: ReactNode;
    title: string;
    /** Area and list the record belongs to, shown above the title. */
    breadcrumbs?: PaloBreadcrumb[];
    /** Whom the document is with; takes the place of the generic kind. */
    subject?: ReactNode;
    fallbackUrl?: string;
    pdfUrl?: string;
    /**
     * A mask that opens its PDF its own way (the order saves first) hands it
     * in here; the eye beside the primary action then uses it (#2316).
     */
    preview?: PaloDetailPreview;
    /** Entries of the overflow menu that only this mask knows about. */
    menuItems?: PaloOverflowAction[];
    /** Arrows through the records of the list one came from. */
    pager?: ReactNode;
    connectorWidth?: string;
    /** Extra nodes in the header actions row, e.g. the inquiry rating. */
    extraActions?: ReactNode;
    /** The document's own next step (complete, cancel), left of save. */
    flowActions?: ReactNode;
    /** See PaloSaveAction: set where the mask has a server baseline to compare against. */
    comparesToServer?: boolean;
    /** The mask's own next step saves too and stays the filled one (see PaloFlowAction `savesFirst`). */
    saveBehindFlow?: boolean;
    /**
     * Whether a superadmin may unlock a closed document for editing.
     *
     * The purchase order says no: a completed or cancelled order is corrected
     * by cancelling it and writing a new one, for every role (#2217). Offering
     * the entry would promise something the server no longer does.
     */
    unlockable?: boolean;
    /** Right-hand rail (PaloSideRail) beside the form. */
    dock?: ReactNode;
    /** The document's history (PaloHistory), below the form. */
    history?: ReactNode;
    /** Body below the header: the wizard's step-content portal target etc. */
    children?: ReactNode;
    onUnlockedChange: (unlocked: boolean) => void;
    onIndexChange: (index: number) => void;
    onGlobalStateChange: (state: Partial<TEntity>) => void;
    onManualSave: () => void;
    onDelete?: (id: number) => void;
    onAlertChange?: (alert: ReceiptAlert | null) => void;
    //  data can be any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCtaButtonChanged: (enabled: boolean, data: any) => void;
    onTransition?: (transitionObject: Transition, target: TransitionTarget) => void;
    renderNumber?: (entity: TEntity) => ReactNode;
    /** The wording of the status, where a kind of document has its own (#2264). */
    statusTranslationId?: (status: TEntity['status']) => string;
    poolRef?: ((ref: PaloDetailHeaderBase<TEntity>) => void) | RefObject<PaloDetailHeaderBase<TEntity>>;
}

type PaloDetailHeaderProps<TEntity extends EntityType> = MyPaloDetailHeaderProps<TEntity> & WithRouterProps;

interface PaloDetailHeaderState {
    warningDialogVisible: boolean;
    convertReceiptVisible: boolean;
    previewVisible: boolean;
    index: number;
}

/**
 * PALO shell for the shared document wizard (inquiry, purchase order): the
 * prototype's Belegmaske chrome around the untouched PoolStepper engine. Same
 * props and imperative API as the legacy DetailHeader, plus body children and
 * the bottom activity dock.
 */
class PaloDetailHeaderBase<TEntity extends EntityType> extends Component<
    PaloDetailHeaderProps<TEntity>,
    PaloDetailHeaderState
> {
    public state: PaloDetailHeaderState = {
        warningDialogVisible: false,
        convertReceiptVisible: false,
        previewVisible: false,
        index: 0,
    };

    // Components lose types on style wrapping
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private stepper: any;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setStepperRef = (ref: any) => (this.stepper = ref);

    private handleWarningOpen = () => {
        this.setState({ warningDialogVisible: true });
    };

    private handleWarningClose = () => {
        this.setState({ warningDialogVisible: false });
    };

    private handleConvertReceiptOpen = () => {
        this.setState({ convertReceiptVisible: true });
    };

    private handleConvertReceiptClose = () => {
        this.setState({ convertReceiptVisible: false });
    };

    private handleDelete = () => {
        this.setState({ warningDialogVisible: false });
        if (this.props.entity && this.props.onDelete) {
            this.props.onDelete(this.props.entity.id);
        }
    };

    private handleUnlockChanged = () => {
        this.props.onUnlockedChange(!this.props.unlocked);
    };

    private onIndexChange = (index: number) => {
        this.setState({ index });
        if (this.props.onIndexChange && typeof this.props.onIndexChange === 'function') {
            this.props.onIndexChange(index);
        }
    };

    private onGlobalStateChange = (state: Partial<TEntity>) => {
        this.props.onGlobalStateChange(state);
    };

    private onSave = async () => {
        if (await this.stepper.canActivateAfterTriedToActivate()) {
            this.props.onManualSave();
            return true;
        }
        return false;
    };

    private onShowPdf = async () => {
        if (this.props.pdfUrl && (await this.onSave())) {
            this.setState({ previewVisible: true });
        }
    };

    public async canActivateAfterTriedToActivate() {
        return await this.stepper.canActivateAfterTriedToActivate();
    }

    public async TriggerManualSave() {
        return await this.onSave();
    }

    public componentDidMount() {
        if (this.props.poolRef) {
            if (typeof this.props.poolRef == 'function') {
                this.props.poolRef?.(this);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.props.poolRef as any).current = this;
        }
    }

    public UNSAFE_componentWillReceiveProps(props: PaloDetailHeaderProps<TEntity>) {
        this.setState(state => {
            return {
                index: props.stepIndex || state.index,
            };
        });
    }

    public render() {
        const { entity, isSuperAdmin, title, converterAvailable, extraActions, flowActions, fallbackUrl } = this.props;
        const { warningDialogVisible, convertReceiptVisible, previewVisible } = this.state;

        const phase = paloDocumentPhase(entity?.status, {
            saved: !!entity?.id,
            unlocked: this.props.unlocked,
        });

        /**
         * Everything but the next step. Deleting used to sit as a red button
         * between "PDF" and "Speichern"; here it takes two clicks and never the
         * one meant for something else (#2206).
         */
        const menuActions: PaloOverflowAction[] = [
            ...(this.props.onTransition && !converterAvailable
                ? [
                      {
                          label: <FormattedMessage id="app.menu.action.transition" />,
                          onClick: this.handleConvertReceiptOpen,
                          disabled: true,
                          reason: this.props.transitionBlockedReason,
                      },
                  ]
                : []),
            ...(this.props.menuItems ?? []),
            // Printing is not the next step of any document, so it sits with
            // everything else behind the three dots. Its own button stood
            // between the step and saving and was reached by mistake (#2206).
            ...(typeof this.props.pdfUrl === 'string'
                ? [
                      {
                          label: <FormattedMessage id="app.common.previewPrint" />,
                          onClick: this.onShowPdf,
                          disabled: !(entity && entity.id),
                      },
                  ]
                : []),
            ...(false !== this.props.unlockable && isSuperAdmin && entity && entity.status !== DocumentStatus.Created
                ? [
                      {
                          label: <FormattedMessage id={this.props.unlocked ? 'app.label.lock' : 'app.label.unlock'} />,
                          onClick: this.handleUnlockChanged,
                      },
                  ]
                : []),
            ...(this.props.onDelete
                ? [
                      {
                          label: <FormattedMessage id="app.common.delete" />,
                          onClick: this.handleWarningOpen,
                          disabled: !entity || entity.status !== DocumentStatus.Created,
                          danger: true,
                      },
                  ]
                : []),
        ];

        const actions = (
            <>
                {extraActions}
                {/* Only while it is the step that is due: on a document still
                    being written, making the follow-up stood there greyed out
                    beside the action the user was aiming for. Until then it
                    waits in the menu (#2206). */}
                {this.props.onTransition && converterAvailable && (
                    <PaloFlowAction
                        phase={phase}
                        kind="followUp"
                        startIcon={<TransitionIcon />}
                        onClick={this.handleConvertReceiptOpen}
                    >
                        <FormattedMessage id="app.menu.action.transition" />
                    </PaloFlowAction>
                )}
                {flowActions}
            </>
        );

        const primaryAction = (
            <PaloSaveAction
                phase={phase}
                comparesToServer={this.props.comparesToServer}
                behind={this.props.saveBehindFlow}
                onSave={this.onSave}
            />
        );

        return (
            <>
                {/* Saving without reaching for the footer — the one move that
                    happens on every mask, several times per document. */}
                <PaloShortcutBinding
                    combo="s"
                    meta={true}
                    group="Beleg"
                    label="Speichern"
                    handler={() => {
                        void this.onSave();
                    }}
                />
                {/* The same move as the back arrow, including the warning about
                    unsaved changes — Esc must not be a quieter way out. */}
                <PaloShortcutBinding
                    combo="Escape"
                    group="Beleg"
                    label="Zurück zur Liste"
                    handler={() => {
                        // A dialog answers Esc itself; going back as well would
                        // close it and leave the mask in one keystroke.
                        if (document.querySelector('.MuiModal-root, .MuiDialog-root')) {
                            return;
                        }

                        if (fallbackUrl) {
                            this.props.navigate(fallbackUrl);
                        } else {
                            this.props.navigate(-1);
                        }
                    }}
                />
                <PaloDetailView
                    themeContent={false}
                    // Back goes to the list, not the way one came: the head
                    // says where a document sits, not how it was reached, and
                    // coming from another document the arrow led there instead
                    // of out (#2212, R-19).
                    onBack={() => (fallbackUrl ? this.props.navigate(fallbackUrl) : this.props.navigate(-1))}
                    number={
                        entity
                            ? this.props.renderNumber
                                ? this.props.renderNumber(entity)
                                : padZeroLeft(entity.id, 5)
                            : undefined
                    }
                    title={<FormattedMessage id={title} />}
                    status={
                        entity ? (
                            <PaloStatusPill
                                status={entity.status}
                                label={
                                    <FormattedMessage
                                        id={(this.props.statusTranslationId ?? getDocumentStatusTranslationId)(
                                            entity.status
                                        )}
                                    />
                                }
                            />
                        ) : undefined
                    }
                    breadcrumbs={this.props.breadcrumbs}
                    actions={actions}
                    menu={<PaloOverflowMenu actions={menuActions} />}
                    preview={
                        this.props.preview ??
                        (typeof this.props.pdfUrl === 'string'
                            ? {
                                  onOpen: () => void this.onShowPdf(),
                                  disabled: !(entity && entity.id),
                                  disabledHint: <FormattedMessage id="app.common.previewAfterSave" />,
                              }
                            : undefined)
                    }
                    pager={this.props.pager}
                    primaryAction={primaryAction}
                    alert={
                        <PaloDocumentNotices alert={entity?.receiptAlert} onAlertChange={this.props.onAlertChange} />
                    }
                    tabs={
                        <PoolStepper
                            variant="tabs"
                            poolRef={this.setStepperRef}
                            steps={this.props.steps}
                            stepsDone={this.props.stepsDone}
                            enforceStepOrder={this.props.enforceStepOrder}
                            index={this.state.index}
                            contentPortal={this.props.contentPortal}
                            initialState={this.props.stepperInitialState}
                            unlocked={this.props.unlocked}
                            connectorWidth={this.props.connectorWidth}
                            onIndexChange={this.onIndexChange}
                            onGlobalStateChange={this.onGlobalStateChange}
                            onCtaButtonChanged={this.props.onCtaButtonChanged}
                        />
                    }
                    dock={this.props.dock}
                    history={this.props.history}
                >
                    {this.props.children}
                </PaloDetailView>
                <PaloConfirmationDialog
                    isVisible={warningDialogVisible}
                    title={<FormattedMessage id="app.common.deleteHeader" />}
                    action={<FormattedMessage id="app.common.delete" />}
                    onCancel={this.handleWarningClose}
                    onConfirm={this.handleDelete}
                >
                    <FormattedMessage id="app.common.deleteWarning" />
                </PaloConfirmationDialog>
                <PaloDocumentPreview
                    url={previewVisible ? this.props.pdfUrl : undefined}
                    title={<FormattedMessage id="app.common.preview" />}
                    openLabel={<FormattedMessage id="app.common.openInNewTab" />}
                    closeLabel={<FormattedMessage id="app.common.close" />}
                    onClose={() => this.setState({ previewVisible: false })}
                />
                <ConverterReceiptModal
                    isVisible={convertReceiptVisible}
                    source={entity as TransitionSource}
                    onClose={this.handleConvertReceiptClose}
                    onConvert={this.props.onTransition}
                />
            </>
        );
    }
}

export const PaloDetailHeader = withRouter(PaloDetailHeaderBase) as <TEntity extends EntityType>(
    props: MyPaloDetailHeaderProps<TEntity>
) => ReactElement;
